import React, { useCallback } from 'react'
import { hooks, I18n, CDNSVG } from '@front/volcanion'

import { TextComponent, TextField, MenuSelectorField, ModelAutocompleteField, ModelMenuSelectorField, ModelMenuSelectorComponent } from '@front/squirtle'
import { Typography, Box, Collapse, Grid } from '@mui/material'

import UserUtils from '@front/volcanion/utils/user'
import FormatUtils from '@front/squirtle/utils/format'

const Row1 = ({
  user,
  searchGSM,
  normalizePhone,
  onSearchCompanyservice,
  onSearchCompanycontract,
  companycontract_id,
  allowServicePrecision,
  hasPassword,
  disabled
}) =>
  <Grid container spacing={1} size={12}>
    <Grid size="grow">
      <TextField
        name='user.gsm'
        label={I18n.t('phone.label', { count: 1 })}
        afterChange={searchGSM}
        normalize={normalizePhone}
        debounceTime={250}
        disabled={disabled}
      />
    </Grid>
    <Grid size="grow">
      <ModelAutocompleteField
        name={'user.companycontract'}
        label={I18n.t('contract.label', { count: 1 })}
        model_name={'companycontract'}
        searchMode='search'
        selectFirst={false}
        afterChange={onSearchCompanycontract}
        loadOnFocus={!!user}
        loadOnMount={!!user}
        selectSingleOption
        config={{
          initital_filter: { companyservices: { companyusers: { user } } },
          mutator: contract => ({ label: _.join([contract?.client_id, contract?.name], '-'), ...contract }),
        }}
        autocompleteMutator={q => (
          !q ? { companyservices: { companyusers: { user } } }
            : { or: [{ client_id: { startsWith: q } }, { name: { startsWith: q } }] }
        )
        }
        InputProps={{
          inputProps: { autoComplete: "new-password" }
        }}
        getOptionIcon={option => 'circle'}
        getOptionIconProps={option => ({
          base: 'material-icons',
          size: 10,
          sx: { color: FormatUtils.getStatusColor(FormatUtils.getStatus(option)) }
        })
        }
        getOptionProps={option => !!option && ({ disabled: FormatUtils.getStatus(option) !== 'active' })}
        autoFocus
        clearValue={null}
        required
        disabled={disabled}
      />
    </Grid>
    <Grid size="grow">
      <ModelAutocompleteField
        name={'user.companyservice'}
        label={I18n.t('companyservice.label', { count: 1 })}
        model_name={'companyservice'}
        searchMode='search'
        searchQuery={!!allowServicePrecision ? '=' : 'startsWith'}
        loadOnFocus={!!allowServicePrecision}
        autocompleteKey={'client_id'}
        labelKeys={['client_id']}
        multiple={false}
        autocompleteMutator={q => ({
          contract: companycontract_id,
          companyusers: !!user && !q ? { user } : undefined,
          client_id: !!allowServicePrecision ? q : { startsWith: q }
        })}
        afterChange={onSearchCompanyservice}
        selectSingleOption={!!companycontract_id}
        InputProps={{
          inputProps: { autoComplete: "new-password" }
        }}
        getOptionIcon={option => 'circle'}
        getOptionIconProps={option => ({
          base: 'material-icons',
          size: 10,
          sx: { color: FormatUtils.getStatusColor(FormatUtils.getStatus(option)) }
        })
        }
        getOptionProps={option => !!option && ({ disabled: FormatUtils.getStatus(option) !== 'active' })}
        required
        selectFirst={false}
        disabled={disabled}
      />
    </Grid>
    <Grid hidden={!hasPassword} size="grow">
      <TextField
        label={I18n.t('password.label', { count: 1 })}
        name={'user.password'}
        InputProps={{
          inputProps: { autoComplete: "new-password" }
        }}
        disabled={disabled}
      />
    </Grid>
  </Grid>

const Row2 = ({
  company_id,
  require_confirmation,
  load_type,
  user,
  commercial_formula,
  application,
  service,
  handleChangeRidemode,
  disabled
}) =>
  <Grid container spacing={1} size={12}>
    <Grid size="grow">
      <ModelMenuSelectorComponent
        label={I18n.t('company.label', { count: 1 })}
        model_name={'company'}
        labelKeys={['name']}
        value={company_id}
        config={{ ids: [company_id] }}
        multiple={false}
        disabled
      />
    </Grid>
    <Grid size="grow">
      <ModelMenuSelectorField
        name={'user.companycontract'}
        label={I18n.t('contract.label', { count: 1 })}
        model_name={'companycontract'}
        labelKeys={['name']}
        searchMode='search'
        loadOnMount
        multiple={false}
        disabled
      />
    </Grid>
    <Grid size="grow">
      <ModelMenuSelectorField
        name={'user.companyservice'}
        label={<Box sx={{ display: !!require_confirmation ? 'flex' : 'none' }}>
          <Box sx={{ mr: 1 }}> <CDNSVG name='call_wait' /> </Box>
          <Typography> {I18n.t('companyservice.label', { count: 1 })} </Typography>
        </Box>
        }
        model_name={'companyservice'}
        labelKeys={['name']}
        searchMode='search'
        multiple={false}
        loadOnMount
        disabled
      />
    </Grid>
    <Grid size="grow">
      <ModelMenuSelectorField
        name='user.commercial_formula'
        label={I18n.t('commercial_formula.label', { count: 1 })}
        model_name='commercialformula'
        labelKeys={['name']}
        loadOnMount
        readOnly
        disabled
      />
    </Grid>
    <Grid size="grow">
      <ModelMenuSelectorField
        name={'ridemode'}
        label={I18n.t('ride.label', { count: 1 })}
        afterUserChange={handleChangeRidemode}
        model_name={'ridemode'}
        labelKeys={['name_translated']}
        searchMode={'search'}
        model_func='getAuthorized'
        required
        config={{
          sort: ['display_index ASC'],
          forced_filter: {
            load_type,
            user,
            commercial_formula,
            application,
            service
          },
          watchers: [
            load_type,
            user,
            commercial_formula,
            application,
            service
          ],
          validate_filter: useCallback(() => !!load_type && !!application, [load_type, application])
        }}
        disabled={disabled}
      />
    </Grid>
  </Grid >

const Row3 = ({
  phoneValue,
  onSearchCompanyuser,
  onLoadTypeChange,
  guest_booking,
  getCompanyuserFilter,
  getCompanyuserValidate,
  companyuser_watchers,
  disabled
}) =>
  <Grid container spacing={1} size={12}>
    <Grid size="grow">
      <ModelMenuSelectorField
        name='user.companyuser'
        label={I18n.t('companyuser.label', { count: 1 })}
        model_name='companyuser'
        searchMode='search'
        disabled={guest_booking === false || !!disabled}
        loadOnFocus
        config={{
          mutator: companyuser => ({
            label: UserUtils.getFormatedName({ ...companyuser?.contact, designation: companyuser?.user?.customerinfo?.name }),
            ...companyuser
          }),
          populate: ['user.customerinfo', 'user.info', 'user.auth', 'contact'],
          validate_filter: getCompanyuserValidate,
          forced_filter: getCompanyuserFilter(...companyuser_watchers),
          watchers: companyuser_watchers,
          default_limit: 100
        }}
        afterUserChange={onSearchCompanyuser}
        getOptionIcon={option => 'circle'}
        getOptionIconProps={option => ({
          base: 'material-icons',
          size: 10,
          sx: { color: FormatUtils.getStatusColor(FormatUtils.getStatus(option)) }
        })}
        noneLabel={I18n.t('void.label', { count: 1 })}
        noneFirst
        selectFirst
        getOptionProps={option => !!_.get(option, 'companyuser_id') && ({ disabled: FormatUtils.getStatus(option) !== 'active' })}
      />
    </Grid>
    <Grid size="grow">
      <TextComponent
        value={phoneValue}
        label={I18n.t('phone.label', { count: 1 })}
        disabled
      />
    </Grid>
    <Grid hidden size="grow">
      <ModelMenuSelectorField
        name='user.application'
        label={I18n.t('application.label', { count: 1 })}
        model_name='application'
        labelKeys={['name']}
        selectFirst
        config={{
          allowAll: true,
          sortBy: ['display_index'],
          sortDirection: ['asc']
        }}
        disabled={disabled}
      />
    </Grid>
    <Grid size="grow">
      <MenuSelectorField
        name='load_type'
        label={I18n.t('load.label', { count: 1 })}
        options={hooks.useLoads()}
        selectFirst
        afterUserChange={onLoadTypeChange}
        required
        disabled
      />
    </Grid>
  </Grid >

const Row4 = () =>
  <Grid size={12}>
    <ModelMenuSelectorField
      name={'user.companycontract'}
      label={I18n.t('address.label', { count: 1 })}
      model_name={'companycontract'}
      config={{ populate: ['contact'] }}
      labelKeys={['contact.address']}
      searchMode='search'
      multiple={false}
      disabled
      required
    />
  </Grid>

const Row5 = () =>
  <Grid size={12}>
    <ModelMenuSelectorField
      name={'user.companyservice'}
      label={I18n.t('comment.label', { count: 1 })}
      model_name={'companyservice'}
      config={{
        populate: ['comments.type'],
        mutator: companyservice => ({
          label: _.head(_.compact(
            _.map(_.get(companyservice, 'comments'), comment => (
              _.get(comment, 'type.client_id') === 'service_operator'
                ? _.get(comment, 'text')
                : false
            )))),
          ...companyservice
        })
      }}
      labelKeys={['label']}
      searchMode='search'
      multiple={false}
      disabled
      required
    />
  </Grid>

const B2CSection = ({ hasCompanyAccess, ...rest }) => <>
  <Grid container spacing={1} rowSpacing={1.5} alignItems='center'>
    <Row1 {...rest} />
  </Grid>
  <Collapse in={hasCompanyAccess}>
    <Grid container spacing={1} rowSpacing={1.5} sx={{ alignItems: 'center', mt: 1 }}>
      <Row2 {...rest} />
      <Row3 {...rest} />
      <Row4 />
      <Row5 />
    </Grid>
  </Collapse>
</>

export default React.memo(B2CSection)
